import type { UserAuth } from '@repo/common';
import type { AssetCommonItem } from '@repo/common/types/Adapter';
import type {
  Asset,
  AssetAttachment,
  AssetContentHistory,
  AssetData,
  AssetRow,
  AssetType
} from '@repo/common/types/Asset';
import type { BaseModel } from '@repo/common/types/Sqlite';
import { safeToISOString } from '@repo/toolslib/src/utils/date.util';
import { parse, stringify } from '@repo/toolslib/src/utils/serialize.util';
import { PrivateClientDatabaseAdapter } from '../private.client.adapter';
import type { DeduplicationStrategy } from '../types';

const SUMMARY_MAX_LENGTH = 500;

const parseJson = <T>(value: string | null | undefined, fallback: T): T => {
  if (value == null || value === '') {
    return fallback;
  }
  try {
    return parse(value) as T;
  } catch {
    return fallback;
  }
};

const coerceAssetType = (value: string | null | undefined): AssetType => {
  if (!value) return 'email';
  return value as AssetType;
};

export class AssetClientAdapter extends PrivateClientDatabaseAdapter<AssetRow, AssetCommonItem> {
  constructor(
    sqliteModel: BaseModel<Asset, AssetRow>,
    userId: string,
    authToken: Pick<UserAuth, 'idToken' | 'refreshToken'>,
    adapterConfig?: Partial<{
      baseUrl: string;
      authToken: string;
      timeout: number;
      retries: number;
    }>
  ) {
    super(sqliteModel, 'asset', userId, authToken, adapterConfig);
  }

  protected sqliteRowToItem(row: AssetRow): AssetCommonItem {
    return {
      id: row.id,
      assetType: coerceAssetType(row.assetType),
      title: row.title,
      summary: row.summary ?? undefined,
      workspaceId: row.workspaceId ?? undefined,
      userId: row.userId ?? this.userId,
      createdAt: row.createdAt ? new Date(row.createdAt) : new Date(),
      updatedAt: row.updatedAt ? new Date(row.updatedAt) : new Date(),
      deletedAt: row.deletedAt ? new Date(row.deletedAt) : undefined,
      isFavorite: row.isFavorite === true || row.isFavorite === 1 || row.isFavorite === 'true',
      content: row.content ?? null,
      contentHistory: parseJson<AssetContentHistory | null>(row.contentHistory, null),
      data: parseJson<AssetData | null>(row.data, null),
      attachments: parseJson<AssetAttachment[] | null>(row.attachments, null)
    };
  }

  protected itemToSqliteRow(item: AssetCommonItem): AssetRow {
    const nowIso = new Date().toISOString();
    return {
      id: item.id,
      assetType: item.assetType,
      title: item.title,
      summary: item.summary ? item.summary.slice(0, SUMMARY_MAX_LENGTH) : null,
      content: item.content ?? null,
      contentHistory: item.contentHistory ? stringify(item.contentHistory) : null,
      data: item.data ? stringify(item.data) : null,
      attachments: item.attachments ? stringify(item.attachments) : null,
      isFavorite: item.isFavorite ? 1 : 0,
      workspaceId: item.workspaceId ?? null,
      userId: item.userId ?? this.userId,
      createdAt: safeToISOString(item.createdAt) || nowIso,
      updatedAt: safeToISOString(item.updatedAt) || nowIso,
      deletedAt: item.deletedAt ? safeToISOString(item.deletedAt, undefined, true) || null : null
    };
  }

  protected async getAllFromSqlite(): Promise<AssetRow[]> {
    const rows = await (this.sqliteModel as any).findAllRows({ paranoid: true });
    return rows.map((row: AssetRow) => this.normalizeRow(row));
  }

  protected async getFromSqliteById(id: string): Promise<AssetRow | null> {
    const rows = await (this.sqliteModel as any).findAllRows({
      where: { id },
      limit: 1,
      paranoid: true
    });
    if (!rows || rows.length === 0) {
      return null;
    }
    return this.normalizeRow(rows[0]);
  }

  private normalizeRow(row: Partial<AssetRow>): AssetRow {
    const toIso = (value: any): string => safeToISOString(value) || new Date().toISOString();

    return {
      id: row.id as string,
      assetType: row.assetType || 'email',
      title: row.title || '',
      summary: row.summary ?? null,
      content: row.content ?? null,
      contentHistory: row.contentHistory ?? null,
      data: row.data ?? null,
      attachments: row.attachments ?? null,
      isFavorite: row.isFavorite ?? 0,
      workspaceId: (row.workspaceId as string | null | undefined) ?? null,
      userId: (row.userId as string | null | undefined) ?? this.userId,
      createdAt: row.createdAt ? toIso(row.createdAt) : new Date().toISOString(),
      updatedAt: row.updatedAt ? toIso(row.updatedAt) : new Date().toISOString(),
      deletedAt: row.deletedAt ? safeToISOString(row.deletedAt, undefined, true) || null : null
    };
  }

  protected async upsertListToSqliteImpl(rows: AssetRow[]): Promise<void> {
    const serialized = rows.map((row) => ({
      ...row,
      contentHistory: row.contentHistory ?? null,
      data: row.data ?? null,
      attachments: row.attachments ?? null
    }));

    await (this.sqliteModel as any).bulkUpsertItemsRaw(serialized);
  }

  protected getDeduplicationStrategy(): DeduplicationStrategy<AssetCommonItem> {
    return {
      type: 'none' as const,
      getKey: (item) => item.id
    };
  }
}
